import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PriceTable from '../components/PriceTable';
import { ServiceIcon } from '../components/icons';
import { SERVICE_CONTENT } from '../data/serviceContent';
import {
  SERVICES as FALLBACK_SERVICES,
  HIDDEN_SERVICE_IDS,
  VISIBLE_SERVICE_IDS,
  AUTO_VEHICLE_TYPES,
  MOTORCYCLE_VEHICLE_TYPES,
  MOTORCYCLE_VISIBLE_SERVICE_IDS,
} from '../data/services';
import { AWS_CONFIG } from '../config/aws';

export default function ServicesPage() {
  const [services, setServices] = useState(FALLBACK_SERVICES);

  useEffect(() => {
    fetch(`${AWS_CONFIG.apiUrl}/services`)
      .then(r => r.json())
      .then(data => {
        if (data.services?.length > 0) {
          // Normalize: API returns serviceId, local uses id
          const normalized = data.services
            .map(s => ({ ...s, id: s.serviceId || s.id }))
            .filter(s => !HIDDEN_SERVICE_IDS.includes(s.id));
          setServices(normalized);
        }
      })
      .catch(() => {}); // fallback to hardcoded
  }, []);

  // 자동차 가격표 — VISIBLE_SERVICE_IDS에 등록된 6개만 (basic-wash·interior-care 제외).
  const autoServices = services.filter(s =>
    VISIBLE_SERVICE_IDS.includes(s.id || s.serviceId)
  );

  // 오토바이 가격표 — MOTORCYCLE_VISIBLE_SERVICE_IDS만 (현재 basic-wash 1개).
  const motorcycleServices = services.filter(s =>
    MOTORCYCLE_VISIBLE_SERVICE_IDS.includes(s.id || s.serviceId)
  );

  return (
    <div>
      <title>서비스 & 가격 — KOA DETAILING | 수원 스팀세차·광택·코팅 요금표</title>
      <meta name="description" content="기본·프리미엄 세차 4만원부터, 프리미엄 광택 35만원~, 유리막 코팅(세라믹) 70만원~, 실내 집중케어. 경차·중형 세단·SUV·승합·오토바이 등 14차종 차종별 가격표. 수원 곡반정 KOA DETAILING." />
      <link rel="canonical" href="https://koa-asia.com/services" />

      {/* Page header */}
      <div className="page-header">
        <div className="container">
          <span className="badge badge-primary" style={{ marginBottom: 16 }}>SERVICES</span>
          <h1>서비스 & 가격</h1>
          <p>전문 스팀 장비와 친환경 약품으로 세심하게 관리합니다</p>
          <div style={{
            maxWidth: 560,
            margin: '16px auto 0',
            textAlign: 'center',
            padding: '10px 16px',
            background: 'rgba(74, 159, 255, 0.08)',
            borderRadius: 8,
            fontSize: '0.95rem',
            lineHeight: 1.5,
            position: 'relative',
          }}>
            <strong style={{ color: 'var(--primary)' }}>엔진룸, 트렁크 세정 기본 포함</strong>
            {/* 자체 줄로 내려 한국어 단어 단위(keep-all)로만 줄바꿈 — 좁은 화면에서도 안 잘림 */}
            <span style={{
              display: 'block', marginTop: 2,
              color: 'var(--text-muted)', fontSize: '0.85rem',
              wordBreak: 'keep-all',
            }}>(차량 상태에 따라 추가금 발생할 수 있습니다)</span>
          </div>
        </div>
      </div>

      {/* Service cards - enhanced */}
      <section className="section">
        <div className="container">
          <div className="feature-grid">
            {services.map((s, i) => {
              const sid = s.id || s.serviceId;
              const hasDetail = !!SERVICE_CONTENT[sid];
              const cardInner = (
                <>
                  <div className="service-card-hero">
                    <ServiceIcon serviceId={sid} fallbackIcon={s.icon} size={88} strokeWidth={1.2} />
                  </div>
                  <div className="service-card-body">
                    <h3 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: 8 }}>
                      {s.name}
                    </h3>
                    <p style={{
                      color: 'var(--text-muted)', fontSize: '0.9rem',
                      marginBottom: 20, lineHeight: 1.6,
                    }}>
                      {s.description}
                    </p>
                    <div style={{
                      background: 'rgba(74, 159, 255, 0.08)',
                      borderRadius: 8, padding: '12px 16px',
                    }}>
                      {(s.prices?.['mid-sedan'] || 0) > 0 ? (
                        <>
                          <span className="stat-number" style={{ fontSize: '1.8rem' }}>
                            ₩{s.prices['mid-sedan'].toLocaleString()}
                          </span>
                          <span style={{ color: 'var(--text-dim)', fontSize: '0.85rem', marginLeft: 4 }}>~</span>
                          <p style={{ color: 'var(--text-dim)', fontSize: '0.8rem', marginTop: 4 }}>
                            중형 기준
                          </p>
                        </>
                      ) : (
                        <span className="stat-number" style={{ fontSize: '1.8rem' }}>문의</span>
                      )}
                    </div>
                    {hasDetail && (
                      <p style={{
                        marginTop: 12, fontSize: '0.8rem', color: 'var(--primary)',
                        fontWeight: 600,
                      }}>
                        자세히 보기 →
                      </p>
                    )}
                  </div>
                </>
              );
              const cardStyle = {
                textAlign: 'center',
                animation: `fadeUp 0.6s ease-out ${i * 0.1}s both`,
                textDecoration: 'none', color: 'inherit',
                display: 'block',
                padding: 0,
              };
              return hasDetail ? (
                <Link key={sid} to={`/services/${sid}`} className="glow-card" style={cardStyle}>
                  {cardInner}
                </Link>
              ) : (
                <div key={sid} className="glow-card" style={cardStyle}>
                  {cardInner}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <div className="container"><div className="gradient-divider" /></div>

      {/* 자동차 가격표 */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <h2 className="section-title">🚗 자동차 차종별 가격</h2>
          <p className="section-subtitle">차량 크기에 따라 가격이 달라집니다</p>
          {autoServices.map(s => (
            <PriceTable
              key={`auto-${s.id || s.serviceId}`}
              service={s}
              vehicleTypes={AUTO_VEHICLE_TYPES}
            />
          ))}
          <p style={{
            textAlign: 'center', color: 'var(--text-dim)',
            fontSize: '0.85rem', marginTop: 32,
          }}>
            * 표시된 가격은 부가세 별도입니다
          </p>
        </div>
      </section>

      <div className="container"><div className="gradient-divider" /></div>

      {/* 오토바이 가격표 — 배기량별 5종 × 노출 서비스 4개 */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <h2 className="section-title">🏍️ 오토바이 배기량별 가격</h2>
          <p className="section-subtitle">125cc 미만 / 쿼터 / 미들 / 리터 / 오버리터급 — 한국 라이더 면허 기준</p>
          {motorcycleServices.map(s => (
            <PriceTable
              key={`moto-${s.id || s.serviceId}`}
              service={s}
              vehicleTypes={MOTORCYCLE_VEHICLE_TYPES}
            />
          ))}
          <p style={{
            textAlign: 'center', color: 'var(--text-dim)',
            fontSize: '0.85rem', marginTop: 32,
          }}>
            * 표시된 가격은 부가세 별도입니다
          </p>
        </div>
      </section>
    </div>
  );
}
